import {
  BrowserMessageReceiveType,
  BrowserMessageType,
  exchangeElectronMessage,
  registerElectronMessageReceiver,
  sendElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useRecoilValue} from 'recoil';

import {LockedDocumentInResponse} from '@lexian/api/documents-api';
import {userState} from '_/state/user';

import FilesInUseDialog from '../components/FilesInUse';
import {
  DialogType,
  getLockedDocumentById,
  getLockedDocuments,
  getWatchedDocumentIds,
  hasWatchedDocuments,
  releaseDocument,
  UnauthorizedError,
} from '../store/filesInUseUtils';
import {logout} from '../utils/logout';

export type UseFilesInUseActions = {
  showFilesInUse: () => Promise<void>;
  onClose: () => Promise<void>;
  onLogout: () => Promise<void>;
  onUpdate: () => Promise<void>;
  clearAllFiles: () => void;
};

export function useFilesInUse(): [
  dialog: React.ReactElement | null,
  actions: UseFilesInUseActions,
] {
  const [dialogType, setDialogType] = useState<DialogType | undefined>(
    undefined
  );
  const [filesInUse, setFilesInUse] = useState<LockedDocumentInResponse[]>([]);
  const [watchedDocumentIds, setWatchedDocumentIds] = useState<
    Set<string> | undefined
  >(undefined);

  // ref to protect doLogout from stale closures of the username
  const loggedInUsernameRef = useRef('');
  loggedInUsernameRef.current = useRecoilValue(userState)?.username ?? '';

  const clearAllFiles = useCallback(() => {
    setDialogType(undefined);
    setFilesInUse([]);
  }, []);

  const doLogout = useCallback(async () => {
    clearAllFiles();
    await logout(loggedInUsernameRef.current);
  }, [clearAllFiles]);

  const quitAnyway = useCallback(() => {
    clearAllFiles();
    sendElectronMessage({
      payload: null,
      type: BrowserMessageType.FORCE_EXIT_APPLICATION,
    });
  }, [clearAllFiles]);

  const quitRegularly = useCallback(() => {
    clearAllFiles();
    sendElectronMessage({
      payload: null,
      type: BrowserMessageType.SAFE_EXIT_APPLICATION,
    });
  }, [clearAllFiles]);

  const doUpdate = useCallback(() => {
    clearAllFiles();
    sendElectronMessage({
      payload: null,
      type: BrowserMessageType.UPDATE_APPLICATION,
    });
  }, [clearAllFiles]);

  // The watched ids are fetched together with the locked documents so the
  // dialog never renders rows with not-yet-loaded watched information
  const fetchLockedAndWatchedDocuments = useCallback(
    async (propagateError?: boolean) => {
      const [files, watchedIds] = await Promise.all([
        getLockedDocuments(propagateError),
        getWatchedDocumentIds(),
      ]);
      setWatchedDocumentIds(watchedIds);
      return {files, watchedIds};
    },
    []
  );

  const refreshLockedDocuments = useCallback(async () => {
    const {files} = await fetchLockedAndWatchedDocuments();
    setFilesInUse(files);
  }, [fetchLockedAndWatchedDocuments]);

  const releaseSingleDocument = useCallback(
    async (document: LockedDocumentInResponse) => {
      await releaseDocument(document);
      setFilesInUse((files) =>
        files.filter(({documentId}) => documentId !== document.documentId)
      );
    },
    []
  );

  const releaseAllStaleDocuments = useCallback(async () => {
    await Promise.all(filesInUse.map((document) => releaseDocument(document)));
    clearAllFiles();
  }, [filesInUse, clearAllFiles]);

  const onClose = useCallback(async () => {
    try {
      const {files, watchedIds} = await fetchLockedAndWatchedDocuments(true);
      if (hasWatchedDocuments(files, watchedIds)) {
        setDialogType('CLOSING_APP');
        setFilesInUse(files);
      } else {
        quitRegularly();
      }
    } catch (error) {
      setDialogType(
        error instanceof UnauthorizedError
          ? 'CLOSING_APP_UNAUTHORIZED'
          : 'CLOSING_APP_UNREACHABLE'
      );
      setFilesInUse([]);
    }
  }, [fetchLockedAndWatchedDocuments, quitRegularly]);

  const onLogout = useCallback(async () => {
    try {
      const {files, watchedIds} = await fetchLockedAndWatchedDocuments(true);
      if (hasWatchedDocuments(files, watchedIds)) {
        setDialogType('LOGGING_OUT');
        setFilesInUse(files);
      } else {
        await doLogout();
      }
    } catch {
      setDialogType('LOGGING_OUT_UNREACHABLE');
      setFilesInUse([]);
    }
  }, [fetchLockedAndWatchedDocuments, doLogout]);

  const onUpdate = useCallback(async () => {
    try {
      const {files, watchedIds} = await fetchLockedAndWatchedDocuments(true);
      if (hasWatchedDocuments(files, watchedIds)) {
        setDialogType('UPDATING_APP');
        setFilesInUse(files);
      } else {
        doUpdate();
      }
    } catch (error) {
      setDialogType(
        error instanceof UnauthorizedError
          ? 'UPDATING_APP_UNAUTHORIZED'
          : 'UPDATING_APP_UNREACHABLE'
      );
      setFilesInUse([]);
    }
  }, [fetchLockedAndWatchedDocuments, doUpdate]);

  const showFilesInUse = useCallback(async () => {
    const {files} = await fetchLockedAndWatchedDocuments();
    setDialogType('FROM_MENU');
    setFilesInUse(files);
  }, [fetchLockedAndWatchedDocuments]);

  // Triggered when a document is locked by another user while opening it
  useEffect(() => {
    const openFileMessageHandler = (event: MessageEvent): void => {
      if (event.data?.type === 'renderer') {
        const {status, data} = event.data.payload;
        if (status === 'file-locked' && data) {
          const {documentId, casusId} = data;
          getLockedDocumentById(documentId, casusId).then((document) => {
            if (document) {
              setDialogType('IN_AN_ACTION');
              setFilesInUse([document]);
            }
          });
        }
      }
    };

    window.addEventListener('message', openFileMessageHandler);
    return () => window.removeEventListener('message', openFileMessageHandler);
  }, []);

  // Triggered when a new user logs in
  useEffect(() => {
    const handleStaleDocumentIds = async (
      staleDocumentIds: string[]
    ): Promise<void> => {
      if (staleDocumentIds.length > 0) {
        const {files} = await fetchLockedAndWatchedDocuments();
        if (files.length > 0) {
          setDialogType('LAST_LOGIN');
          setFilesInUse(
            files.filter(({documentId}) =>
              staleDocumentIds.includes(documentId)
            )
          );
        }
      }
    };

    const userInfoMessageHandler = (event: MessageEvent): void => {
      if (event.data?.type === BrowserMessageType.SEND_USER_INFO) {
        exchangeElectronMessage(
          {
            payload: null,
            type: BrowserMessageType.STALE_DOCUMENT_IDS_QUERY,
          },
          (staleDocumentIds) => {
            handleStaleDocumentIds(staleDocumentIds);
          }
        );
        window.removeEventListener('message', userInfoMessageHandler);
      }
    };

    window.addEventListener('message', userInfoMessageHandler);
    return () => window.removeEventListener('message', userInfoMessageHandler);
  }, [fetchLockedAndWatchedDocuments]);

  // registerElectronMessageReceiver has no unsubscribe, so register once and
  // delegate to the latest onClose through a ref
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    registerElectronMessageReceiver(
      BrowserMessageReceiveType.CLOSE_PREVENTED_BY_OPEN_FILES,
      () => {
        onCloseRef.current();
      }
    );
  }, []);

  const dialog = dialogType ? (
    <FilesInUseDialog
      dialogType={dialogType}
      filesInUse={filesInUse}
      hasFilesInUse={filesInUse.length > 0}
      watchedDocumentIds={watchedDocumentIds}
      onClearAllFiles={clearAllFiles}
      onLogout={doLogout}
      onQuitAnyway={quitAnyway}
      onQuitRegularly={quitRegularly}
      onRefreshLockedDocuments={refreshLockedDocuments}
      onReleaseAllStaleDocuments={releaseAllStaleDocuments}
      onReleaseDocument={releaseSingleDocument}
      onTryClosingAgain={onClose}
      onTryUpdatingAgain={onUpdate}
      onUpdate={doUpdate}
    />
  ) : null;

  return [dialog, {showFilesInUse, onClose, onLogout, onUpdate, clearAllFiles}];
}
